import React, { useEffect, useMemo, useRef, useContext } from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import BuilderCollectionContext from '~/builder/components/BuilderCollectionContext';
import { useProductGridRelatedProductsContext } from '~/grid/context/ProductGridRelatedProductsContext';
import LoyaltyLockIcon from '~/icons/components/LoyaltyLockIcon/LoyaltyLockIcon';
import WhitelabelIcon from '~/icons/components/WhitelabelIcon';
import ProductCallout from '~/shared/components/ProductCallout';
import ProductCellPriceDetails from '~/shared/components/ProductCellPriceDetails';
import WishlistToggleButton from '~/shared/components/WishlistToggleButton';
import { LoyaltyTiers } from '~/shared/constants/loyaltyTiers';
import { productDetailPath } from '~/shared/constants/product';
import { ProductTag } from '~/shared/constants/products';
import { SecondarySites } from '~/shared/constants/secondarySites';
import { isSavage } from '~/shared/utils/brandNameHelper';
import generateSxfCdnImageUrl from '~/shared/utils/generateSxfCdnImageUrl';
import getCartPrice from '~/shared/utils/getCartPrice';
import { isLockedToMember } from '~/shared/utils/loyalty';
import useProductCallout from '~/shared/utils/useProductCallout';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { Router, Link } from '~/techstyle-shared/next-routes';
import { useLoyalty, useMembership } from '~/techstyle-shared/react-accounts';
import { useCart } from '~/techstyle-shared/react-cart';
import {
  ProductCell as BaseProductCell,
  ProductImage,
  ProductName as BaseProductName,
  ProductReview,
  useTabIndex,
  GridColorSwatches,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import {
  ProductType,
  trackProductClicked,
  useProductListViewed,
} from '~/techstyle-shared/react-products';
import {
  buildURL,
  getQueryParametersFromURL,
  useSession,
} from '~/techstyle-shared/redux-core';

import GridSwatches from '../GridSwatches';
import SwatchesLoadingSkeleton from '../SwatchesLoadingSkeleton';

const ProductLinkInner = styled.a`
  display: block;
  width: 100%;

  &:empty {
    display: none;
  }
`;

const ProductLink = ({ href, ...otherProps }) => {
  return (
    <Link href={href}>
      <ProductLinkInner {...otherProps} />
    </Link>
  );
};
ProductLink.propTypes = {
  href: PropTypes.string.isRequired,
};

const Wrapper = styled(BaseProductCell)`
  ${({ theme }) => theme.gridCellStyles.productCellWrapper}
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const LoyaltyLockBackground = styled.div`
  position: absolute;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  background-color: black;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLoyaltyLockIcon = styled(LoyaltyLockIcon)`
  margin-top: -1px;
`;

const ProductSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 8px 0;

  /**
    * temp solution (approved by UAT) to minimize content shift.
    * will be replaced with a better solution per ticket below
    * https://techstyle-prod.atlassian.net/browse/FLD-1747
    */
  min-height: 140px;
`;

const CalloutText = styled(ProductCallout)`
  font-size: 12px;
  text-transform: uppercase;

  ${({ theme }) => theme.gridCellStyles.calloutText};
`;

const AdditionalContent = styled.div`
  color: ${({ theme }) => theme.colors.additionalText};
`;

const FavoritesPlaceholderImage = styled.div`
  background-color: ${({ theme }) =>
    theme.colors.favoritesPlaceholderImageBackground};
  display: block;
  height: 225.14px;
  width: 150px;
  @media (max-width: 768px) {
    height: 354px !important;
    min-width: 100%;
  }
  @media (max-width: 540px) {
    height: 392px !important;
    width: 100%;
  }
  @media (max-width: 414px) {
    height: 297px !important;
    width: 100%;
  }
  @media (max-width: 411px) {
    height: 295px !important;
    width: 100%;
  }
  @media (max-width: 400px) {
    height: 267.92px !important;
    width: 100%;
  }
  @media (max-width: 360px) {
    height: 257px !important;
  }
  @media (max-width: 320px) {
    height: 227px !important;
  }
  @media (max-width: 280px) {
    height: 225px !important;
  }
`;

const promoMessageStyles = css`
  ${({ theme }) => theme.pdpStyles.promoMessageStyles};
`;

const ProductName = styled(BaseProductName)`
  display: block;
  ${({ theme }) => theme.gridCellStyles.productName}
`;

const ratingStyle = css`
  color: ${({ theme }) => theme.colors.textEmphasized};
  & svg {
    width: 12px;
  }
`;

const priceStyle = css`
  ${({ theme }) => theme.gridCellStyles.priceStyles}
`;

const toggleButtonStyle = css`
  padding: 6px;
`;

const ImageProductLink = styled(ProductLink)`
  position: relative;
`;

const StyledProductImage = styled(ProductImage)`
  ${(props) =>
    props.$exclusiveLocked &&
    `
    filter: contrast(30%) brightness(150%);
  `}
`;

const generateSxfFallbackImages = (product) => {
  const defaultSet = ['1', '2', '3', '4', 'LAYDOWN'].map((angle) => {
    const sizes = [6, 7, 8, 9].map((imageSize) => {
      return generateSxfCdnImageUrl({
        product,
        angle,
        withWidthHeight: true,
        imageSize,
      });
    });
    return { angle, sizes };
  });
  return {
    default: defaultSet,
    plus: [],
  };
};

const ProductCell = React.forwardRef(
  (
    {
      product: productFromProps,
      productPricingFromGridContext = {}, // used for getting data-cnstrc-item-price in ProductGrid view (ex. search result page)
      favorites,
      imageAspectRatio,
      imageType,
      productSource,
      href,
      reviewsHref,
      additionalContentHref,
      tabIndex,
      gridIndex,
      fplCode,
      fplId,
      payloadKey,
      showColorsLink = false,
      showGridReviews = false,
      swatchData,
      ...rest
    },
    ref
  ) => {
    const router = useRouter();
    const { isVip } = useMembership();
    tabIndex = useTabIndex(tabIndex);
    const { formatMessage } = useIntl();
    const gridColorSwatchesContext = GridColorSwatches.useContext();
    const { isVipCart } = useCart({ isLoginRequired: true }) || {};

    const { customFields: { gridGender } = {} } =
      useContext(BuilderCollectionContext) || {};

    const secondarySite = useSecondarySite();
    const routePrefix = secondarySite ? `/${secondarySite}` : '';

    const isBundle = productFromProps.productTypeId === ProductType.BUNDLE;
    const showColorSwatches =
      gridColorSwatchesContext &&
      gridColorSwatchesContext.activeProduct &&
      !isBundle &&
      !secondarySite;

    const productAutoTag = isBundle ? { 'data-autotag': 'bundle_product' } : {};

    const product = useMemo(() => {
      if (showColorSwatches) {
        return gridColorSwatchesContext.activeProduct;
      } else {
        return productFromProps;
      }
    }, [gridColorSwatchesContext, productFromProps, showColorSwatches]);

    const { isLoading: isRelatedProductSwatchesLoading } =
      useProductGridRelatedProductsContext() || {};

    const callout = useProductCallout({ product, isGridCallout: true });

    const { isLoggedIn } = useSession();
    const dispatch = useDispatch();
    const { tier } = useLoyalty();
    /* we ran into an issue where the CIO api response for product.tagIdList contains strings only,
    whereas ProductTag.platinumExclusive from the configs is being interpreted as a number.
    In order to ensure isProductPlatinumExclusive is returning the proper value,
    We need to check both cases, since different API endpoints such as V2, GQL, and CIO
    may return tag IDs in different formats */
    const isProductPlatinumExclusive =
      product?.tagIdList?.includes(String(ProductTag.platinumExclusive)) ||
      product?.tagIdList?.includes(Number(ProductTag.platinumExclusive));
    const isLoyaltyLocked =
      isProductPlatinumExclusive &&
      isLockedToMember(tier?.label, LoyaltyTiers.PLATINUM);
    if (isSavage()) {
      if (!product.productImages && product.product_images) {
        product.productImages = product.product_images;
      }

      if (
        product?.productImages?.default?.length === 0 &&
        product?.productImages?.plus?.length === 0
      ) {
        product.productImages = generateSxfFallbackImages(product);
      }
    }

    if (!href) {
      href = buildURL(
        `${routePrefix}/products/${
          product.permalink || product.masterProductId
        }`,
        {
          psrc: productSource,
          gridGender,
        }
      );
    }
    if (!reviewsHref) {
      reviewsHref = `${href}#reviews`;
    }

    const hasPromo =
      Boolean(product.promos?.promoCode) &&
      typeof product.promos?.promoCode === 'string';

    const productLabelDisplay = hasPromo
      ? formatMessage({
          id: product.promos.promoCode,
          defaultMessage: product.promos.promoCode,
        })
      : null;

    const productInfo = useMemo(
      () => ({
        product: {
          ...product,
          promos: {
            ...product.promos,
            promoLabel: product.promos?.promoLabel || productLabelDisplay,
          },
        },
        href: href.split(/[#?]/)[0],
        gridIndex,
        fplId,
        fplCode,
        productSource,
        payloadKey,
      }),
      [
        product,
        productLabelDisplay,
        href,
        gridIndex,
        fplId,
        fplCode,
        productSource,
        payloadKey,
      ]
    );

    const handleClick = (event) => {
      const { href } = event.currentTarget;
      const mpid = product?.masterProductId;
      const pageUrl = router.asPath;
      // Attempt to get the existing session storage item for the given URL
      const existingItem = sessionStorage.getItem(pageUrl);
      // Create a new scroll position object with the given mpid value
      const scrollPosition = { mpid };
      // If an item already exists, parse it and modify the mpid property
      if (existingItem) {
        const parsedItem = JSON.parse(existingItem);
        parsedItem.mpid = mpid; // Update the mpid property
        sessionStorage.setItem(pageUrl, JSON.stringify(parsedItem)); // Save the modified item
      } else {
        // If no item exists, store the new scroll position object in session storage
        sessionStorage.setItem(pageUrl, JSON.stringify(scrollPosition));
      }

      dispatch(
        trackProductClicked({
          ...productInfo,
          productUrl: href,
        })
      );
      // Without this check, users will get kicked out of the SPA
      if (!(event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        const { href } = event.currentTarget;
        const pathname = href?.includes('/products/')
          ? `${routePrefix}/${productDetailPath}/[identifier]`
          : href;
        setTimeout(() => {
          Router.push(
            {
              pathname,
              query: getQueryParametersFromURL(href),
            },
            href
          );
        }, 300);
      }
    };

    const { observe, unobserve } = useProductListViewed();

    useEffect(() => {
      const node = productCellRef.current;
      if (node) {
        observe(node, productInfo);
        return () => unobserve(node);
      }
    }, [observe, unobserve, productInfo]);

    const productCellRef = useRef();
    const { reviewCount } = product;

    const additionalContentComponent =
      !favorites && callout && secondarySite !== SecondarySites.OUTLET ? (
        <AdditionalContent>
          <ProductLink
            href={additionalContentHref || href}
            tabIndex={additionalContentHref ? tabIndex : -1}
            onClick={handleClick}
          >
            <CalloutText type={callout.type} value={callout.value} />
          </ProductLink>
        </AdditionalContent>
      ) : null;

    const constructorItemPrice =
      getCartPrice({ product, isVip, isVipCart, secondarySite }) ||
      getCartPrice({
        product: productPricingFromGridContext,
        isVip,
        isVipCart,
        secondarySite,
      });

    const constructorTags = {
      'data-cnstrc-item-id': product.masterProductId,
      'data-cnstrc-item-name': product.label,
      'data-cnstrc-item-price': constructorItemPrice,
    };

    return (
      <Wrapper
        {...rest}
        product={product}
        ref={productCellRef || ref}
        data-autotag={
          reviewCount > 0
            ? 'prodgrid-prod-with-rating'
            : 'pdp-modal-styledSelect-list'
        }
        data-mpid={product.masterProductId}
        {...constructorTags}
        data-testid="product-cell"
      >
        <ImageProductLink
          href={href}
          aria-label={product.label}
          tabIndex={tabIndex}
          onClick={handleClick}
          {...productAutoTag}
        >
          {favorites && !product.productImages ? (
            <FavoritesPlaceholderImage />
          ) : (
            <>
              <StyledProductImage
                aspectRatio={imageAspectRatio}
                type={imageType}
                aria-label={product.label}
                role="img"
                $exclusiveLocked={isLoyaltyLocked}
              />
              {isLoyaltyLocked && (
                <LoyaltyLockBackground>
                  <StyledLoyaltyLockIcon width={28} height={28} color="white" />
                </LoyaltyLockBackground>
              )}
            </>
          )}
        </ImageProductLink>
        {isLoggedIn && !secondarySite ? (
          <ImageOverlay>
            <WishlistToggleButton
              toggleButtonStyle={toggleButtonStyle}
              data-cnstrc-btn="add-to-favorites"
            />
          </ImageOverlay>
        ) : null}

        <ProductSection>
          {showColorSwatches &&
            (isRelatedProductSwatchesLoading ? (
              <SwatchesLoadingSkeleton
                primarySwatchUrl={productFromProps.swatchUrl}
              />
            ) : (
              <GridSwatches href={href} swatchData={swatchData} />
            ))}
          <ProductLink href={href} tabIndex={-1} onClick={handleClick}>
            <ProductName />
            <ProductCellPriceDetails
              product={productFromProps}
              favorites={favorites}
              promoMessageStyles={promoMessageStyles}
              priceStyle={priceStyle}
            />
          </ProductLink>
          <ProductLink
            href={reviewsHref}
            onClick={handleClick}
            tabIndex={reviewsHref === href ? -1 : tabIndex}
          >
            {showGridReviews && (
              <ProductReview
                ratingStyle={ratingStyle}
                hideReviewCount
                emptyIcon={<WhitelabelIcon iconName="emptyStar" />}
                fullIcon={<WhitelabelIcon iconName="filledStar" />}
              />
            )}
          </ProductLink>
          {additionalContentComponent}
        </ProductSection>
      </Wrapper>
    );
  }
);

ProductCell.displayName = 'ProductCell';

ProductCell.propTypes = {
  additionalContentHref: PropTypes.string,
  children: PropTypes.node,
  favorites: PropTypes.bool,
  fplCode: PropTypes.string,
  fplId: PropTypes.number,
  gridIndex: PropTypes.number,
  href: PropTypes.string,
  imageAspectRatio: PropTypes.number,
  imageType: PropTypes.oneOf(['default', 'plus']),
  payloadKey: PropTypes.string,
  product: PropTypes.object.isRequired,
  productPricingFromGridContext: PropTypes.object,
  productSource: PropTypes.string,
  reviewsHref: PropTypes.string,
  showColorsLink: PropTypes.bool,
  showGridReviews: PropTypes.bool,
  swatchData: PropTypes.array,
  tabIndex: PropTypes.number,
};

export default ProductCell;
